// src/ctxReflector/components/Instances/views/Licensed/UnlicensedPage.tsx
import React, { useState, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { GenericTable } from '@ui-components/Table/GenericTable'
import GenericButton from '@ui-components/Button/GenericButton'
import { GenericFilter } from '@ui-components/Filter/GenericFilter'
import GenericAlert from '@ui-components/Alert/GenericAlert'
import { useProcessingOverlay } from '@hooks/useProcessingOverlay'
import { UnlicensedInstance, columns as baseColumns } from './UnlicensedData'
import {
    fetchUnlicensedInstances as getAllInstances,
    deleteLicensedInstance as removeDevice,
} from './request/request'
import { useAxiosAuth0 } from '@axios-config/useAxiosAuth0'

function UnlicensedPage() {
    const { isAuthenticated, isLoading } = useAuth0()
    const api = useAxiosAuth0()
    const [instances, setInstances] = useState<UnlicensedInstance[]>([])
    const [isFetching, setIsFetching] = useState(false)

    const [searchTerm, setSearchTerm] = useState('')
    const [searchColumn, setSearchColumn] = useState('All')
    const searchOptions = ['All', 'Name', 'Description', 'ID', 'Deactivate']

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
    const [deleteConfirmInput, setDeleteConfirmInput] = useState('')
    const [instanceToDelete, setInstanceToDelete] = useState<UnlicensedInstance | null>(null)

    const [isResultAlertOpen, setIsResultAlertOpen] = useState(false)
    const [alertVariant, setAlertVariant] = useState<'success' | 'error'>('success')
    const [alertTitle, setAlertTitle] = useState('')
    const [alertDescription, setAlertDescription] = useState('')
    const overlay = useProcessingOverlay(isFetching)

    useEffect(() => {
        if (isLoading || !isAuthenticated) return
        loadInstances()
    }, [isLoading, isAuthenticated])

    const loadInstances = async () => {
        try {
            setIsFetching(true)
            const resp = await getAllInstances(api)
            setInstances(resp.data || [])
        } catch (error) {
            console.error('Error fetching Unlicensed Instances =>', error)
        } finally {
            setIsFetching(false)
        }
    }

    const handleRefresh = () => {
        loadInstances()
    }

    const filteredInstances = instances.filter((row) => {
        const term = searchTerm.toLowerCase().trim()
        if (!term) return true
        const includes = (val: string) => val.toLowerCase().includes(term)
        switch (searchColumn) {
            case 'Name':
                return includes(row.name)
            case 'Description':
                return includes(row.description)
            case 'ID':
                return includes(row.id)
            case 'Deactivate':
                return includes(row.deactivate)
            default:
                return (
                    includes(row.name) ||
                    includes(row.description) ||
                    includes(row.id) ||
                    includes(row.deactivate)
                )
        }
    })

    const handleDeleteClick = (rowData: UnlicensedInstance) => {
        setInstanceToDelete(rowData)
        setDeleteConfirmInput('')
        setIsDeleteConfirmOpen(true)
    }

    const handleConfirmDelete = async () => {
        if (!instanceToDelete) return
        if (deleteConfirmInput.trim().toLowerCase() !== 'confirm') {
            setIsDeleteConfirmOpen(false)
            showLicenseSafe()
            return
        }
        setIsDeleteConfirmOpen(false)
        try {
            setIsFetching(true)
            await removeDevice(api, instanceToDelete.id)  // <--- otra vez, le pasamos 'api'
            setInstances(prev => prev.filter(inst => inst.id !== instanceToDelete.id))
            setAlertVariant('success')
            setAlertTitle('Deleted!')
            setAlertDescription(`La instancia "${instanceToDelete.name}" se ha eliminado.`)
        } catch (err) {
            console.error('Error removing device =>', err)
            setAlertVariant('error')
            setAlertTitle('Error')
            setAlertDescription('No se pudo eliminar la instancia, intente de nuevo.')
        } finally {
            setIsFetching(false)
            setInstanceToDelete(null)
            setIsResultAlertOpen(true)
        }
    }

    const showLicenseSafe = () => {
        setAlertVariant('success')
        setAlertTitle('Your Device is safe!')
        setAlertDescription('No se realizó ningún cambio.')
        setIsResultAlertOpen(true)
    }

    const finalColumns = baseColumns.map((col) => {
        if (col.header === 'Actions' && col.renderCell) {
            return {
                ...col,
                renderCell: (row: UnlicensedInstance) => (
                    <button
                        className="
              border border-semantic-border-secondary
              rounded
              bg-semantic-bg-danger-secondary
              text-semantic-fg-danger
              hover:bg-red-100
              w-10 h-10 flex items-center justify-center
            "
                        onClick={() => handleDeleteClick(row)}
                    >
                        {/* ícono de basura */}
                    </button>
                ),
            }
        }
        return col
    })

    return (
        <div className="relative p-4">
            <div className="flex items-center justify-end mb-4 gap-2">
                <GenericFilter
                    searchTerm={searchTerm}
                    onSearchTermChange={setSearchTerm}
                    searchColumn={searchColumn}
                    onSearchColumnChange={setSearchColumn}
                    columnOptions={searchOptions}
                />
                <GenericButton label="Refresh" variant="secondary" onClick={handleRefresh} />
            </div>

            <GenericTable
                columns={finalColumns}
                data={filteredInstances}
                initialRowsPerPage={5}
                rowsPerPageOptions={[5, 10, 20]}
                width="100%"
                tableClassName="bg-semantic-bg-primary"
            />

            <GenericAlert
                isOpen={isDeleteConfirmOpen}
                onClose={() => setIsDeleteConfirmOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Are you sure?"
                description="Once deleted, you will not be able to recover this Device! Enter 'confirm' to continue..."
                cancelLabel="Cancel"
                confirmLabel="Yes, delete it!"
                variant="warning"
                confirmButtonDisabled={deleteConfirmInput.trim().toLowerCase() !== 'confirm'}
            >
                <div className="mt-4">
                    <input
                        className="border border-semantic-border-secondary rounded px-2 py-1 w-full"
                        placeholder="confirm"
                        value={deleteConfirmInput}
                        onChange={(e) => setDeleteConfirmInput(e.target.value)}
                    />
                </div>
            </GenericAlert>

            <GenericAlert
                isOpen={isResultAlertOpen}
                onClose={() => setIsResultAlertOpen(false)}
                onConfirm={() => setIsResultAlertOpen(false)}
                title={alertTitle}
                description={alertDescription}
                cancelLabel="Close"
                confirmLabel="OK"
                variant={alertVariant}
            />

            {overlay}
        </div>
    )
}

export default UnlicensedPage
